import {tokens, Toggle, ToggleItem, ToggleItemConfig} from '@sail/ui';
import {message, useIntl} from '@sail/intl';
import type {FundingMethod} from 'src/v2/state/types';

const messages = {
  fundingMethodLabel: message({
    id: 'crypto.payins.v2.refund.funding_method_label',
    description: 'Label for the funding method selector',
    defaultMessage: 'Funding method',
  }),

  nonCustodialWallet: message({
    id: 'crypto.payins.v2.refund.funding_method_wallet',
    description: 'Funding method option for a self-custody wallet',
    defaultMessage: 'Non-custodial wallet',
  }),

  nonCustodialWalletDescription: message({
    id: 'crypto.payins.v2.refund.funding_method_wallet_description',
    description: 'Examples of non-custodial wallets',
    defaultMessage: 'Base, Phantom, MetaMask...',
  }),

  exchange: message({
    id: 'crypto.payins.v2.refund.funding_method_exchange',
    description: 'Funding method option for a centralized exchange',
    defaultMessage: 'Exchange',
  }),

  exchangeDescription: message({
    id: 'crypto.payins.v2.refund.funding_method_exchange_description',
    description: 'Examples of centralized exchanges',
    defaultMessage: 'Coinbase, Binance, Kraken, Crypto.com...',
  }),
};

// The Sail Toggle highlights the selected item with the brand/action.primary
// color. Match SelectionCard's neutral accent instead: a neutral-500 border on
// hover and when selected, with a neutral fill and dark label on selection.
const selectedBorderColor = tokens.color.neutral[500].toString();

const neutralSelectedToggleItem = ToggleItemConfig.customize({
  css: {
    // Neutral border on hover (base Toggle only changes the background).
    ':hover:not([data-disabled=true])': {
      keyline: selectedBorderColor,
    },
    '[data-checked=true]': {
      keyline: selectedBorderColor,
      // Base Toggle thickens the selected keyline to 'medium'; keep it 'small'
      // so the selected border matches the hover/default (and SelectionCard) width.
      keylineWidth: 'small',
      backgroundColor: 'offset',
    },
    ':hover[data-checked=true]:not([data-disabled=true])': {
      keyline: selectedBorderColor,
      keylineWidth: 'small',
      backgroundColor: 'offset',
    },
  },
  subviews: {
    label: {css: {color: 'text'}},
  },
});

type FundingMethodSectionProps = {
  fundingMethod: FundingMethod | null;
  onFundingMethodChange: (method: FundingMethod) => void;
};

export const FundingMethodSection = ({
  fundingMethod,
  onFundingMethodChange,
}: FundingMethodSectionProps) => {
  const intl = useIntl();

  return (
    <Toggle
      label={intl.formatMessage(messages.fundingMethodLabel)}
      layout="vertical"
      selectedValue={fundingMethod ?? undefined}
      onSelectionChange={(value) =>
        onFundingMethodChange(value as FundingMethod)
      }
      uses={[neutralSelectedToggleItem]}
    >
      <ToggleItem
        value="exchange"
        label={intl.formatMessage(messages.exchange)}
        description={intl.formatMessage(messages.exchangeDescription)}
      />
      <ToggleItem
        value="wallet"
        label={intl.formatMessage(messages.nonCustodialWallet)}
        description={intl.formatMessage(messages.nonCustodialWalletDescription)}
      />
    </Toggle>
  );
};
